/**
 * QuotePolicySettingsPanel — TipTap WYSIWYG for customer quote policy HTML.
 * Loads/publishes via /quotes/policy (independent of generic settings Form).
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Space,
  Switch,
  Typography,
  message,
} from "antd";
import {
  EyeOutlined,
  SaveOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import styled from "styled-components";
import {
  getQuotePolicy,
  publishQuotePolicy,
  previewQuotePolicyPdf,
  openPdfBlob,
} from "../services/quotePolicyService.js";

const { Title, Text, Paragraph } = Typography;

const PanelRoot = styled.div`
  width: 100%;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FieldBlock = styled.div`
  margin-bottom: 16px;
`;

const FieldLabel = styled(Text)`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
`;

const EditorShell = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;

  &:focus-within {
    border-color: #005249;
    box-shadow: 0 0 0 2px rgba(0, 82, 73, 0.12);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const ToolbarButton = styled.button`
  border: 1px solid transparent;
  background: ${(p) => (p.$active ? "#e6f3f1" : "transparent")};
  color: ${(p) => (p.$active ? "#005249" : "#595959")};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 13px;
  cursor: pointer;
  min-height: 28px;

  &:hover {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const EditorBody = styled.div`
  .ProseMirror {
    min-height: 220px;
    max-height: 480px;
    overflow-y: auto;
    padding: 12px 14px;
    outline: none;
    font-size: 14px;
    line-height: 1.6;
  }

  .ProseMirror p {
    margin: 0 0 0.75em;
  }

  .ProseMirror ul,
  .ProseMirror ol {
    padding-left: 1.4em;
    margin: 0 0 0.75em;
  }

  .ProseMirror a {
    color: #005249;
    text-decoration: underline;
  }

  .ProseMirror h2,
  .ProseMirror h3 {
    margin: 0.5em 0 0.4em;
    color: #003d35;
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
`;

/**
 * TipTap toolbar (native buttons — TipTap requires them inside the editor chrome).
 */
function PolicyEditorToolbar({ editor }) {
  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", previous);
    if (url === null) return;
    if (!String(url).trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: String(url).trim() })
      .run();
  };

  return (
    <Toolbar>
      <ToolbarButton
        type="button"
        $active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        Bold
      </ToolbarButton>
      <ToolbarButton
        type="button"
        $active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        Italic
      </ToolbarButton>
      <ToolbarButton
        type="button"
        $active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        H2
      </ToolbarButton>
      <ToolbarButton
        type="button"
        $active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        Bullets
      </ToolbarButton>
      <ToolbarButton
        type="button"
        $active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        Numbered
      </ToolbarButton>
      <ToolbarButton
        type="button"
        $active={editor.isActive("link")}
        onClick={setLink}
      >
        Link
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      >
        Clear
      </ToolbarButton>
    </Toolbar>
  );
}

const QuotePolicySettingsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [title, setTitle] = useState("Customer Moving Policy");
  const [enabled, setEnabled] = useState(false);
  const [version, setVersion] = useState(null);
  const [htmlDraft, setHtmlDraft] = useState("<p></p>");
  const [loadError, setLoadError] = useState(null);
  const [dirty, setDirty] = useState(false);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: htmlDraft,
    onUpdate: ({ editor: ed }) => {
      setHtmlDraft(ed.getHTML());
      setDirty(true);
    },
  });

  const applyPolicy = useCallback(
    (policy) => {
      if (!policy) return;
      setTitle(policy.title || "Customer Moving Policy");
      setEnabled(Boolean(policy.enabled));
      setVersion(policy.version || null);
      const html = policy.html || "<p></p>";
      setHtmlDraft(html);
      if (editor) {
        editor.commands.setContent(html, { emitUpdate: false });
      }
      setDirty(false);
    },
    [editor]
  );

  const loadPolicy = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const policy = await getQuotePolicy();
      applyPolicy(policy);
    } catch (error) {
      setLoadError(error.message || "Failed to load quote policy");
    } finally {
      setLoading(false);
    }
  }, [applyPolicy]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  // Sync loaded HTML once TipTap editor mounts
  useEffect(() => {
    if (!editor || loading) return;
    const current = editor.getHTML();
    if (htmlDraft && current !== htmlDraft && !dirty) {
      editor.commands.setContent(htmlDraft, { emitUpdate: false });
    }
  }, [editor, htmlDraft, loading, dirty]);

  const handlePublish = async () => {
    if (!editor) return;
    const html = editor.getHTML();
    if (!String(title || "").trim()) {
      message.error("Policy title is required");
      return;
    }
    setSaving(true);
    try {
      const policy = await publishQuotePolicy({
        html,
        title: String(title).trim(),
        enabled,
      });
      applyPolicy(policy);
      message.success(
        enabled
          ? "Policy published and enabled for new quotes"
          : "Policy saved (disabled — customers will not be prompted)"
      );
    } catch (error) {
      message.error(error.message || "Failed to publish policy");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!editor) return;
    setPreviewing(true);
    try {
      const blob = await previewQuotePolicyPdf({
        html: editor.getHTML(),
        title: String(title || "").trim(),
      });
      openPdfBlob(blob);
    } catch (error) {
      message.error(error.message || "Failed to preview PDF");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <PanelRoot>
      <PanelHeader>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <FileProtectOutlined style={{ marginRight: 8 }} />
            Customer quote policy
          </Title>
          <Paragraph type="secondary" style={{ margin: "8px 0 0" }}>
            WYSIWYG policy shown to customers before booking confirmation.
            Deposit can still be paid; booking stays blocked until they agree.
          </Paragraph>
        </div>
        <Space wrap>
          <Button
            icon={<EyeOutlined />}
            onClick={handlePreview}
            loading={previewing}
            disabled={loading || !editor}
          >
            Preview PDF
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handlePublish}
            loading={saving}
            disabled={loading || !editor}
          >
            Publish
          </Button>
        </Space>
      </PanelHeader>

      {loadError && (
        <Alert
          type="error"
          showIcon
          message={loadError}
          action={
            <Button size="small" onClick={loadPolicy}>
              Retry
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {version && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Published version: {version}
          {dirty ? " · unsaved edits" : ""}
        </Text>
      )}

      <MetaRow>
        <FieldBlock style={{ flex: "1 1 280px", marginBottom: 0 }}>
          <FieldLabel>Title</FieldLabel>
          <Input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setDirty(true);
            }}
            placeholder="Customer Moving Policy"
            disabled={loading}
          />
        </FieldBlock>
        <FieldBlock style={{ marginBottom: 0 }}>
          <FieldLabel>Enabled</FieldLabel>
          <Switch
            checked={enabled}
            onChange={(checked) => {
              setEnabled(checked);
              setDirty(true);
            }}
            disabled={loading}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
        </FieldBlock>
      </MetaRow>

      <FieldBlock>
        <FieldLabel>Policy content</FieldLabel>
        <EditorShell>
          <PolicyEditorToolbar editor={editor} />
          <EditorBody>
            <EditorContent editor={editor} />
          </EditorBody>
        </EditorShell>
      </FieldBlock>
    </PanelRoot>
  );
};

export default QuotePolicySettingsPanel;
